import { Component, computed, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from '../../../shared/components/breadcrumbs/breadcrumbs';
import { DialogRef, ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { ReimburseUtils } from '../../../shared/services/reimburse-utils';
import { UsersService } from '../../../shared/services/users.service';
import { SearchParams } from '../../../shared/services/voice-testing.service';
import { VoiceTestResult } from '../../../shared/models/voice-testing.model';
import { Role } from '../../../shared/models/user.model';
import { VoiceTestingService } from '../../../shared/services/voice-testing.service';
import { VtFormComponent } from '../vt-form/vt-form.component';
import { VtResultsTableComponent } from '../vt-results-table/vt-results-table.component';
import { VtSearchFormComponent } from '../vt-search-form/vt-search-form.component';

@Component({
  standalone: false,
  selector: 'app-vt-index',
  templateUrl: 'vt-index.component.html',
  styleUrls: ['vt-index.component.scss'],
})
export class VtIndexComponent implements OnInit {
  @ViewChild(VtFormComponent, { static: false }) form: VtFormComponent;
  @ViewChild(VtResultsTableComponent, { static: false }) results: VtResultsTableComponent;
  @ViewChild(VtSearchFormComponent, { static: false }) filter: VtSearchFormComponent;
  @ViewChild('reimburseModalTpl', { read: TemplateRef, static: false }) reimburseModalTpl: TemplateRef<HTMLTemplateElement>;

  pauseUpdateForm = false;
  pauseUpdateResults = false;

  testingDisabled = null;
  isAdmin = false;

  readonly selectedRows = signal<VoiceTestResult[]>([]);
  readonly hasReimbursableTests = computed(() => this.selectedRows().some(row => ReimburseUtils.canReimburse(row)));

  constructor(
    public breadcrumbs: BreadcrumbService,
    public router: Router,
    public route: ActivatedRoute,
    public notificationService: NotificationService,
    public userService: UsersService,
    private modal: ModalService,
    private voiceTestingService: VoiceTestingService,
    titleService: Title
  ) {
    titleService.setTitle('Manual СLI testing');
  }

  afterSaveTestCase() {
    this.pauseUpdateResults = false;
    this.results.currentPageNumber = 1;
    this.results.update();
  }

  ngOnInit() {
    this.breadcrumbs.root('Dashboard', ['dashboard']).add('Test results', ['voice-testing'], true);
    this.userService.getAuthUser().then(user => {
      this.isAdmin = user.role === Role.ADMIN;
    });
  }

  onSubmitFilter(event: SearchParams) {
    if (this.results.loading) {
      return;
    }
    this.results.searchParams = event;
    this.results.currentPageNumber = 1;
    this.results.update();
  }

  batchClear() {
    this.selectedRows.set([]);
    this.results.resetBatch();
  }

  onBatchReimburse(): void {
    const dialogRef: DialogRef = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.reimburseModalTpl).open();
    dialogRef.result.then(result => {
      if (!result) {
        this.notificationService.info('Reimbursement canceled', 'Voice Testing');
        return;
      }

      const reimbursableTests = this.selectedRows().filter(row => ReimburseUtils.canReimburse(row));
      const testIds = reimbursableTests.map(row => row.id);

      this.results.loading = true;
      this.voiceTestingService.reimburse(testIds).subscribe({
        next: () => {
          this.notificationService.success('Tests reimbursed', 'Voice Testing');
          this.results.update();
          this.batchClear();
        },
        error: e => {
          this.notificationService.error({
            title: 'Reimburse tests',
            message: 'An error occurred while reimbursing the tests',
            serviceName: 'VTC',
            requestMessage: e.statusText,
            requestCode: e.status,
            ts: e.timestamp ? e.timestamp : null,
          });
          this.results.loading = false;
        },
      });
    });
  }
}
